// src/pages/accounting/collateralReceived/hooks/useCollateralReceivedForm.js
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import { formatDateForApi } from '../../../../utils/dateUtils';
import { toastService } from '../../../../utils/toastService';
import { useCreateCollateralReceivedMutation, useUpdateCollateralReceivedMutation } from '../../../../api/endpoints/collateralReceived';

// docs/CollateralReceived-API.md §2: "Para birimi ... Varsayılan TRY seçili gelmeli."
const getEmptyForm = (sapDealerId = null) => ({
    id: null,
    sapDealerId,
    numbers: '',
    purchaseDate: null,
    expiryDate: null,
    extensionDate: null,
    enteredAmount: null,
    currency: 'TRY',
    type: null,
    reason: '',
    validity: 'GEÇERLİ',
    bankName: null,
    bankBranchName: null,
    // İade edildi mi? — sadece düzenleme ekranında gösterilir/gönderilir (bkz. save() ve
    // CollateralReceivedFormDialog). Create'te BE otomatik "2" (Hayır) açıyor.
    isReturned: null,
    returnDate: null
});

// Zeynep Danış (Finans BE, 2026-09-07): "isReturned" lookup kodu ("1" = Evet, "2" = Hayır),
// XXBOS_AFTER_YES_NO grubundan string olarak gelir/gider.
const COLLATERAL_RETURNED_YES_CODE = '1';

// BE'nin noktasız 'I' dönme ihtimaline karşı validity'yi normalize eder.
const normalizeValidityValue = (value) => {
    if (typeof value !== 'string') return value;
    const upper = value.trim().toUpperCase().replace(/I/g, 'İ');
    return upper === 'GEÇERLİ' || upper === 'GEÇERSİZ' ? upper : value;
};

/**
 * Alınan Teminatlar ekle/düzenle dialog'unun tüm state + doğrulama + kaydetme mantığı.
 * Bilinçli tercih: dealer-autocomplete mantığı burada, hook katmanında yaşar — dialog
 * component'i (`CollateralReceivedFormDialog`) yalnızca sunumdur. (Projede `AddStockDialog` /
 * `PackageDefinitionsForm` gibi örnekler bu tür mantığı component içine gömüyor; bkz. rapor.)
 *
 * Not: `bankBranchName` serbest metin alanıdır — ayrı bir "banka şubeleri" ucu/cascading
 * dropdown yok (bankaya göre şube listesi çekmeye gerek kalmadı).
 */
export default function useCollateralReceivedForm({ options, onSaved }) {
    const { th } = useLanguage();
    const { hasManagePermission, hasFinanceTeamRole } = usePermissions();
    const canBrowseAllDealers = hasManagePermission('UpdateRole') || hasFinanceTeamRole();
    const { sapDealerId: ownSapDealerId } = useSelector(selectUserParams);

    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState(getEmptyForm(canBrowseAllDealers ? null : ownSapDealerId));
    const [errors, setErrors] = useState({});

    const setField = useCallback((field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    }, []);

    const schema = useMemo(
        () =>
            Yup.object().shape({
                sapDealerId: Yup.mixed().required(th('collateralReceived.validation.dealerRequired')),
                numbers: Yup.string().trim().max(50, th('collateralReceived.validation.numbersMax')).required(th('collateralReceived.validation.numbersRequired')),
                purchaseDate: Yup.date().nullable().required(th('collateralReceived.validation.purchaseDateRequired')),
                // `validity` artık serbest metin değil — sabit iki değerli (GEÇERLİ/GEÇERSİZ) radio,
                // alan disabled (bkz. CollateralReceivedFormDialog) - kullanıcı elle giremiyor ama form her zaman
                // bu iki değerden biriyle (varsayılan/kayıttaki mevcut değer) dolu geldiği için required kalabilir.
                // NOT: eski 'süreli teminatta vade zorunlu' kuralı (validity === 'SURELI') artık hiç tetiklenmiyor —
                // validity bu iki yeni değerden birini taşıyor, 'SURELI' değerini asla almıyor.
                validity: Yup.string()
                    .oneOf(['GEÇERLİ', 'GEÇERSİZ'], th('collateralReceived.validation.validityRequired'))
                    .required(th('collateralReceived.validation.validityRequired')),
                expiryDate: Yup.date()
                    .nullable()
                    .when('validity', {
                        is: (val) => typeof val === 'string' && val.trim().toUpperCase() === 'SURELI',
                        then: (s) =>
                            s
                                .required(th('collateralReceived.validation.expiryDateRequired'))
                                .test('after-purchase', th('collateralReceived.validation.expiryDateBeforePurchase'), function (value) {
                                    const { purchaseDate } = this.parent;
                                    if (!value || !purchaseDate) return true;
                                    return new Date(value) >= new Date(purchaseDate);
                                }),
                        otherwise: (s) => s.nullable()
                    }),
                extensionDate: Yup.date()
                    .nullable()
                    .test('after-base', th('collateralReceived.validation.extensionDateBeforeBase'), function (value) {
                        if (!value) return true;
                        const { purchaseDate, expiryDate } = this.parent;
                        const base = expiryDate || purchaseDate;
                        if (!base) return true;
                        return new Date(value) >= new Date(base);
                    }),
                enteredAmount: Yup.number()
                    .transform((value, originalValue) => (originalValue === '' || originalValue == null ? null : value))
                    .typeError(th('collateralReceived.validation.enteredAmountRequired'))
                    .moreThan(0, th('collateralReceived.validation.enteredAmountPositive'))
                    .max(99999999999.9999, th('collateralReceived.validation.enteredAmountMax'))
                    .required(th('collateralReceived.validation.enteredAmountRequired')),
                currency: Yup.string().required(th('collateralReceived.validation.currencyRequired')),
                type: Yup.mixed().required(th('collateralReceived.validation.typeRequired')),
                reason: Yup.string().trim().max(500, th('collateralReceived.validation.reasonMax')).nullable(),
                bankName: Yup.string().required(th('collateralReceived.validation.bankNameRequired')),
                bankBranchName: Yup.string().trim().max(500, th('collateralReceived.validation.bankBranchNameMax')).required(th('collateralReceived.validation.bankBranchNameRequired')),
                isReturned: Yup.string().nullable(),
                returnDate: Yup.date()
                    .nullable()
                    .when('isReturned', {
                        is: (val) => val === COLLATERAL_RETURNED_YES_CODE,
                        then: (s) => s.required(th('collateralReceived.validation.returnDateRequired')),
                        otherwise: (s) => s.nullable()
                    })
            }),
        [th]
    );

    const openAdd = useCallback(() => {
        setEditMode(false);
        setForm(getEmptyForm(canBrowseAllDealers ? null : ownSapDealerId));
        setErrors({});
        setVisible(true);
    }, [canBrowseAllDealers, ownSapDealerId]);

    const openEdit = useCallback((row) => {
        if (!row) return;
        setEditMode(true);
        setForm({
            id: row.id ?? null,
            sapDealerId: row.sapDealerId != null ? String(row.sapDealerId) : null,
            dealerName: row.dealerName ?? null,
            numbers: row.numbers ?? '',
            purchaseDate: row.purchaseDate ? new Date(row.purchaseDate) : null,
            expiryDate: row.expiryDate ? new Date(row.expiryDate) : null,
            extensionDate: row.extensionDate ? new Date(row.extensionDate) : null,
            enteredAmount: row.enteredAmount ?? null,
            currency: row.currency ?? null,
            type: row.type ?? null,
            reason: row.reason ?? '',
            validity: normalizeValidityValue(row.validity) ?? 'GEÇERLİ',
            bankName: row.bankName ?? null,
            bankBranchName: row.bankBranchName ?? null,
            isReturned: row.isReturned != null ? String(row.isReturned) : '2',
            returnDate: row.returnDate ? new Date(row.returnDate) : null
        });
        setErrors({});
        setVisible(true);
    }, []);

    const close = useCallback(() => {
        setVisible(false);
        setErrors({});
    }, []);

    const [createCollateralReceived, { isLoading: creating }] = useCreateCollateralReceivedMutation();
    const [updateCollateralReceived, { isLoading: updating }] = useUpdateCollateralReceivedMutation();
    const saving = creating || updating;

    const save = useCallback(async () => {
        try {
            await schema.validate(form, { abortEarly: false });
            setErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError.inner?.forEach((err) => {
                if (err.path && !nextErrors[err.path]) nextErrors[err.path] = err.message;
            });
            setErrors(nextErrors);
            return false;
        }

        const payload = {
            ...(editMode ? { id: form.id } : {}),
            sapDealerId: form.sapDealerId,
            numbers: form.numbers.trim(),
            purchaseDate: formatDateForApi(form.purchaseDate),
            expiryDate: form.expiryDate ? formatDateForApi(form.expiryDate) : null,
            extensionDate: form.extensionDate ? formatDateForApi(form.extensionDate) : null,
            enteredAmount: Number(form.enteredAmount),
            currency: form.currency,
            type: Number(form.type),
            reason: form.reason?.trim() || null,
            validity: form.validity?.trim(),
            bankName: form.bankName,
            bankBranchName: form.bankBranchName?.trim(),
            // Create'te iade bilgisi gönderilmiyor — BE kaydı otomatik Hayır ("2") açıyor.
            ...(editMode
                ? {
                      isReturned: form.isReturned ?? '2',
                      returnDate: form.isReturned === COLLATERAL_RETURNED_YES_CODE ? formatDateForApi(form.returnDate) : null
                  }
                : {})
        };

        try {
            if (editMode) {
                await updateCollateralReceived(payload).unwrap();
                toastService.success(th('collateralReceived.updateSuccess'), th('common.success'));
            } else {
                await createCollateralReceived(payload).unwrap();
                toastService.success(th('collateralReceived.addSuccess'), th('common.success'));
            }
            setVisible(false);
            onSaved?.();
            return true;
        } catch (error) {
            toastService.error(error?.data?.errorMessage || th(editMode ? 'collateralReceived.updateError' : 'collateralReceived.addError'), th('common.error'));
            return false;
        }
    }, [schema, form, editMode, createCollateralReceived, updateCollateralReceived, onSaved, th]);

    return {
        visible,
        editMode,
        form,
        errors,
        setField,
        dealerFieldDisabled: !canBrowseAllDealers,
        currencyOptions: options?.currencies ?? [],
        openAdd,
        openEdit,
        close,
        save,
        saving
    };
}
